import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { WorldGrid } from '../../systems/world/WorldGrid';
import { World } from '../../systems/ecs/World';
import { TERRAIN_TYPES } from '../../constants/terrain';
import { TerrainType } from '../../types/terrain'; // Add this import

export interface WorldState {
  isGenerating: boolean;
  isGenerated: boolean;
  seed: string;
  mapData: {
    width: number;
    height: number;
    tiles: number[]; // Store terrain type indices instead of TerrainType objects
  };
  entities: {
    id: string;
    components: Record<string, any>;
  }[];
  selectedEntityId: string | null;
  map: TerrainType[][]; // This is now properly typed
}

const initialState: WorldState = {
  isGenerating: false,
  isGenerated: false,
  seed: '',
  mapData: {
    width: 0,
    height: 0,
    tiles: [],
  },
  entities: [],
  selectedEntityId: null,
  map: [], // Initialize the map property
};

const worldSlice = createSlice({
  name: 'world',
  initialState,
  reducers: {
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
    },
    setIsGenerated: (state, action: PayloadAction<boolean>) => {
      state.isGenerated = action.payload;
    },
    setSeed: (state, action: PayloadAction<string>) => {
      state.seed = action.payload;
    },
    setMapData: (state, action: PayloadAction<WorldState['mapData']>) => {
      state.mapData = action.payload;
    },
    setEntities: (state, action: PayloadAction<WorldState['entities']>) => {
      state.entities = action.payload;
    },
    setSelectedEntityId: (state, action: PayloadAction<string | null>) => {
      state.selectedEntityId = action.payload;
    },
  },
});

export const { setIsGenerating, setIsGenerated, setSeed, setMapData, setEntities, setSelectedEntityId } = worldSlice.actions;

// Add these functions at the end of the file
function generateWorld(seed?: number): World {
  const defaultTerrain = TERRAIN_TYPES[0];
  const grid = new WorldGrid(100, 100, defaultTerrain);
  // Populate the grid with terrain based on the seed
  return new World(grid); // Remove the seed parameter here
}

function serializeWorld(world: World): WorldState {
  const grid = world.getGrid();
  const width = grid.getWidth();
  const height = grid.getHeight();
  const tiles: number[] = [];
  const map: TerrainType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: TerrainType[] = [];
    for (let x = 0; x < width; x++) {
      const tile = grid.getTile(x, y);
      if (tile) { // Add a null check
        tiles.push(TERRAIN_TYPES.indexOf(tile));
        row.push(tile);
      } else {
        tiles.push(-1); // Or some default value
        row.push(TERRAIN_TYPES[0]); // Use default terrain
      }
    }
    map.push(row);
  }

  return {
    isGenerating: false,
    isGenerated: true,
    seed: (world as any).getSeed().toString(),
    mapData: {
      width,
      height,
      tiles,
    },
    entities: world.getEntities().map(entity => ({
      id: entity.getId(),
      components: entity.getAllComponents(),
    })),
    selectedEntityId: null,
    map,
  };
}

export const generateWorldThunk = createAsyncThunk(
  'world/generateWorld',
  async (seed?: number) => {
    const world = generateWorld(seed);
    return serializeWorld(world);
  }
);

export default worldSlice.reducer;